/**
 * `useRouter` over TanStack Router, shaped like Next's Pages Router hook
 * (see ./types.ts). `query` merges path params with the string-parsed search
 * params, so `router.query.id` on /federal-tax/$id and `router.query.session_id`
 * both keep working unchanged.
 */
import { useLocation, useNavigate, useParams, useRouter as useTanStackRouter } from '@tanstack/react-router'
import { useMemo } from 'react'

import { resolveTarget } from './target'
import type { AppRouter, NavigationTarget, NavigationTransitionOptions } from './types'

/**
 * Public surface of the navigation shim: pages import their navigation
 * primitives from `@/lib/navigation/*` and nowhere else (apps/app/CLAUDE.md),
 * so these type names are part of that contract.
 *
 * @public
 */
export type { AppRouter, NavigationTarget, ParsedQuery, UrlObject } from './types'

export function useRouter(): AppRouter {
  const router = useTanStackRouter({ warn: false }) as ReturnType<typeof useTanStackRouter> | undefined
  if (!router) throw new Error('useRouter must be used inside a <RouterProvider>')
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams({ strict: false }) as Record<string, string>

  return useMemo<AppRouter>(() => {
    const go = async (target: NavigationTarget, replace: boolean, options?: NavigationTransitionOptions) => {
      const resolved = resolveTarget(target, window.location.origin)
      // Deliberately not awaited: TanStack's navigate() promise NEVER settles
      // when a blocker (see ./before-navigate) cancels the navigation, so
      // awaiting it would hang every caller that guards a route. Next resolved
      // push()/replace() as the transition started, which is what this returns.
      void Promise.resolve(
        navigate({
          to: resolved.to,
          search: resolved.search,
          hash: resolved.hash,
          replace,
          resetScroll: options?.scroll ?? true,
        } as never),
      ).catch(() => {
        // A rejected navigation is the router's to report, not the caller's.
      })
      return true
    }
    return {
      pathname: location.pathname,
      asPath: location.href,
      query: {
        ...(location.search as Record<string, string | string[]>),
        ...params,
      },
      isReady: true,
      push: (url, _as, options) => go(url, false, options),
      replace: (url, _as, options) => go(url, true, options),
      prefetch: async (url) => {
        const resolved = resolveTarget(url, window.location.origin)
        try {
          await router.preloadRoute({
            to: resolved.to,
            search: resolved.search,
          } as never)
        } catch {
          // Best-effort warm-up, exactly like Next's prefetch.
        }
      },
      back: () => router.history.back(),
      reload: () => window.location.reload(),
    }
  }, [location.href, location.pathname, location.search, navigate, params, router])
}
